import { z } from 'zod';
import { InspectionCornSchema, InspectionEntityCornSchema } from '../base.js';
export const FloorplanKeyDetailsSchema = z.object({
    entityId: InspectionEntityCornSchema,
    inspectionId: InspectionCornSchema,
    floorplanId: z.string(),
});
export const FloorplanSchema = FloorplanKeyDetailsSchema;
export const CapturedFloorplanUnitSchema = z.enum(['m', 'ft']);
export const FloorplanPoint2DSchema = z.object({
    x: z.number(),
    y: z.number(),
});
const FloorplanLineSegmentSchema = z.object({
    start: FloorplanPoint2DSchema,
    end: FloorplanPoint2DSchema,
});
const FloorplanOrientedVolumeSchema = z.object({
    height: z.number(),
    depth: z.number(),
    rotation: z.number(),
});
const FloorplanPlacedObjectSchema = FloorplanOrientedVolumeSchema.extend({
    center: FloorplanPoint2DSchema,
    width: z.number(),
});
const FloorplanRegionSchema = z.object({
    center: FloorplanPoint2DSchema,
    boundary: z.array(FloorplanPoint2DSchema),
});
export const FloorplanBoundarySchema = z.object({
    id: z.string(),
    path: z.array(FloorplanPoint2DSchema),
});
export const FloorplanLabelSchema = z.object({
    text: z.string(),
    position: FloorplanPoint2DSchema,
    boundaryId: z.string().nullable(),
});
export const FloorplanWallCurveSchema = z.object({
    center: FloorplanPoint2DSchema,
    radius: z.number(),
    startAngle: z.number(),
    endAngle: z.number(),
});
export const FloorplanWallSchema = FloorplanLineSegmentSchema.merge(FloorplanOrientedVolumeSchema).extend({
    id: z.string(),
    category: z.literal('wall'),
    length: z.number(),
    curve: FloorplanWallCurveSchema.nullable().optional(),
});
const FloorplanWallChildBaseSchema = FloorplanLineSegmentSchema.merge(FloorplanPlacedObjectSchema).extend({
    id: z.string(),
    parentId: z.string(),
});
const FloorplanElevatedWallChildBaseSchema = FloorplanWallChildBaseSchema.extend({
    baseHeight: z.number().optional(),
});
export const FloorplanDoorSchema = FloorplanWallChildBaseSchema.extend({
    category: z.literal('door'),
    variant: z.enum(['single', 'double', 'sliding']),
});
export const FloorplanWindowSchema = FloorplanElevatedWallChildBaseSchema.extend({
    category: z.literal('window'),
    variant: z.enum(['single', 'double', 'sliding', 'fixed']),
});
export const FloorplanOpeningSchema = FloorplanElevatedWallChildBaseSchema.extend({
    category: z.literal('opening'),
    variant: z.literal('standard'),
});
export const FloorplanWallChildSchema = z.union([
    FloorplanDoorSchema,
    FloorplanWindowSchema,
    FloorplanOpeningSchema,
]);
export const FloorplanSectionSchema = FloorplanRegionSchema.extend({
    id: z.string(),
    label: z.string(),
});
export const FloorplanFurnitureCategorySchema = z.enum([
    'bathtub',
    'chair',
    'dishwasher',
    'fireplace',
    'oven',
    'refrigerator',
    'sink',
    'sofa',
    'stairs',
    'storage',
    'stove',
    'table',
    'television',
    'toilet',
    'washerDryer',
]);
export const FloorplanFurnitureSchema = FloorplanPlacedObjectSchema.extend({
    id: z.string(),
    category: FloorplanFurnitureCategorySchema,
});
const CanonicalCapturedFloorplanSchema = z.object({
    unit: CapturedFloorplanUnitSchema,
    walls: z.array(FloorplanWallSchema),
    doors: z.array(FloorplanDoorSchema),
    windows: z.array(FloorplanWindowSchema),
    openings: z.array(FloorplanOpeningSchema),
    boundaries: z.array(FloorplanBoundarySchema),
    labels: z.array(FloorplanLabelSchema),
    furniture: z.array(FloorplanFurnitureSchema),
});
const SectionBasedCapturedFloorplanSchema = z.object({
    unit: CapturedFloorplanUnitSchema,
    walls: z.array(FloorplanWallSchema),
    doors: z.array(FloorplanDoorSchema),
    windows: z.array(FloorplanWindowSchema),
    openings: z.array(FloorplanOpeningSchema),
    sections: z.array(FloorplanSectionSchema),
    furniture: z.array(FloorplanFurnitureSchema),
});
const normalizeSectionBasedCapturedFloorplan = (floorplan) => ({
    unit: floorplan.unit,
    walls: floorplan.walls,
    doors: floorplan.doors,
    windows: floorplan.windows,
    openings: floorplan.openings,
    boundaries: floorplan.sections.map((section) => ({
        id: section.id,
        path: section.boundary,
    })),
    labels: floorplan.sections.map((section) => ({
        text: section.label,
        position: section.center,
        boundaryId: section.id,
    })),
    furniture: floorplan.furniture,
});
export const CapturedFloorplanSchema = z
    .union([CanonicalCapturedFloorplanSchema, SectionBasedCapturedFloorplanSchema])
    .transform((floorplan) => 'sections' in floorplan ? normalizeSectionBasedCapturedFloorplan(floorplan) : floorplan);
export const CapturedFloorplanUploadSchema = z.object({
    source: z.literal('nutmeg'),
    schemaVersion: z.literal(1),
    label: z.string(),
    timestamp: z.string(),
    floorplan: CapturedFloorplanSchema,
});
export const UploadedFloorplanSchema = FloorplanSchema.merge(CapturedFloorplanUploadSchema);
/** Dynamo item shape: geometry may live inline (legacy) or behind `s3Key`. */
export const UploadedFloorplanRecordSchema = FloorplanSchema.merge(CapturedFloorplanUploadSchema.omit({ floorplan: true }).extend({
    s3Key: z.string().optional(),
    floorplan: CapturedFloorplanSchema.optional(),
})).refine((record) => record.s3Key !== undefined || record.floorplan !== undefined, {
    message: 'UploadedFloorplanRecord requires s3Key or floorplan',
});
